import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('pages.contact.validation.name');
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('pages.contact.validation.email');
    }
    if (!formData.subject.trim()) newErrors.subject = t('pages.contact.validation.subject');
    if (!formData.message.trim()) newErrors.message = t('pages.contact.validation.message');
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitted(true);
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSubmitted(false);
    }, 3000);
  };

  const contactInfo = [
    { icon: '📧', label: t('pages.contact.info.email'), value: t('pages.contact.info.value.email') },
    { icon: '🐦', label: t('pages.contact.info.twitter'), value: t('pages.contact.info.value.twitter') },
    { icon: '📱', label: t('pages.contact.info.instagram'), value: t('pages.contact.info.value.instagram') },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-light-bg">
        {/* Header */}
        <section className="py-12 md:py-20 px-6 md:px-12 bg-gradient-to-b from-light-bg to-light-card">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="font-filson font-bold text-5xl md:text-6xl text-gray-900 mb-4">
              {t('pages.contact.title')}
            </h1>
            <p className="font-filson font-medium text-lg text-gray-600">
              {t('pages.contact.subtitle')}
            </p>
          </motion.div>
        </section>

        {/* Contact Info */}
        <section className="py-12 md:py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card text-center"
                  style={{
                    borderRadius: '12px',
                    border: '1px solid #E7EAE5',
                    padding: '24px',
                    backgroundColor: 'white',
                  }}
                >
                  <p style={{ fontSize: '40px', marginBottom: '12px' }}>
                    {info.icon}
                  </p>
                  <h3 className="font-filson font-bold text-lg text-gray-900 mb-2">
                    {info.label}
                  </h3>
                  <p className="font-filson font-medium text-gray-600">
                    {info.value}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-12 md:py-20 px-6 md:px-12">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card"
              style={{
                borderRadius: '12px',
                border: '1px solid #E7EAE5',
                padding: '32px',
                backgroundColor: 'white',
              }}
            >
              <h2 className="font-filson font-bold text-2xl text-gray-900 mb-6">
                {t('pages.contact.form.title')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label className="font-poppins font-medium text-sm text-gray-700 block mb-2">
                    {t('pages.contact.form.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('pages.contact.form.namePlaceholder')}
                    className="input-bottom-border"
                    style={{
                      borderBottom: errors.name ? '1px solid #CB4B4B' : '1px solid #E7EAE5',
                      padding: '12px 0',
                      fontSize: '1rem',
                      fontFamily: "'Filson Soft', sans-serif",
                      width: '100%',
                    }}
                  />
                  {errors.name && (
                    <p className="text-primary text-sm mt-1 font-filson">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="font-poppins font-medium text-sm text-gray-700 block mb-2">
                    {t('pages.contact.form.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('pages.contact.form.emailPlaceholder')}
                    className="input-bottom-border"
                    style={{
                      borderBottom: errors.email ? '1px solid #CB4B4B' : '1px solid #E7EAE5',
                      padding: '12px 0',
                      fontSize: '1rem',
                      fontFamily: "'Filson Soft', sans-serif",
                      width: '100%',
                    }}
                  />
                  {errors.email && (
                    <p className="text-primary text-sm mt-1 font-filson">{errors.email}</p>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label className="font-poppins font-medium text-sm text-gray-700 block mb-2">
                    {t('pages.contact.form.subject')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('pages.contact.form.subjectPlaceholder')}
                    className="input-bottom-border"
                    style={{
                      borderBottom: errors.subject ? '1px solid #CB4B4B' : '1px solid #E7EAE5',
                      padding: '12px 0',
                      fontSize: '1rem',
                      fontFamily: "'Filson Soft', sans-serif",
                      width: '100%',
                    }}
                  />
                  {errors.subject && (
                    <p className="text-primary text-sm mt-1 font-filson">{errors.subject}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="font-poppins font-medium text-sm text-gray-700 block mb-2">
                    {t('pages.contact.form.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('pages.contact.form.messagePlaceholder')}
                    rows="6"
                    className="input-bottom-border"
                    style={{
                      borderBottom: errors.message ? '1px solid #CB4B4B' : '1px solid #E7EAE5',
                      padding: '12px 0',
                      fontSize: '1rem',
                      fontFamily: "'Filson Soft', sans-serif",
                      width: '100%',
                      resize: 'none',
                    }}
                  />
                  {errors.message && (
                    <p className="text-primary text-sm mt-1 font-filson">{errors.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  whileHover={{ scale: submitted ? 1 : 1.02 }}
                  whileTap={{ scale: submitted ? 1 : 0.98 }}
                  disabled={submitted}
                  className="btn-primary w-full"
                  style={{
                    marginTop: '24px',
                    opacity: submitted ? 0.7 : 1,
                  }}
                >
                  {submitted ? t('pages.contact.form.submit.success') : t('pages.contact.form.send')}
                </motion.button>
              </form>

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
                >
                  <p className="font-filson font-medium text-sm text-green-800">
                    {t('pages.contact.success.message')}
                  </p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-20 px-6 md:px-12 bg-light-card">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-filson font-bold text-4xl text-gray-900 mb-12 text-center"
            >
              {t('pages.contact.faq.title')}
            </motion.h2>

            <div className="space-y-4">
              {t('pages.contact.faq.items', { returnObjects: true }).map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card bg-white"
                  style={{
                    borderRadius: '12px',
                    border: '1px solid #E7EAE5',
                    padding: '20px',
                  }}
                >
                  <h3 className="font-filson font-bold text-gray-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="font-filson font-medium text-gray-600 text-sm">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default ContactPage;
